import { Box, Typography, useTheme } from '@mui/material';

export interface PanelCollapseStripProps {
  /**
   * Which edge the strip sits on, which also decides which side its border
   * renders on: `left` (artifacts-only, standing in for the collapsed RA
   * pane) gets a right border; `right` (ra-only, standing in for the
   * collapsed artifacts pane) gets a left border.
   */
  side: 'left' | 'right';
  /** Persistent vertical text running down the strip, e.g. "Workspace" —
   *  identifies which pane is collapsed. Omit to render an empty strip. */
  visibleLabel?: string;
  /** Test id for the strip wrapper. */
  'data-testid'?: string;
}

const STRIP_WIDTH_PX = 40;

/**
 * Thin full-height vertical strip standing in for a collapsed pane (OR-6123
 * F4 — COLLAPSED ARTIFACTS 63:1850 / COLLAPSED RA 65:10103). Purely a
 * passive label — re-expanding the pane is the panel-layout toggle's job
 * (in the page header), not this strip's; there is no click target here.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const PanelCollapseStrip = ({
  side,
  visibleLabel,
  'data-testid': dataTestId,
}: PanelCollapseStripProps) => {
  const theme = useTheme();
  const borderSide = side === 'left' ? 'borderRight' : 'borderLeft';

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        width: STRIP_WIDTH_PX,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Centered, not flex-start — a prior attempt to match a Figma
        // Layout-panel spec literally (align-items: flex-start) pushed the
        // label too far toward the left edge of the strip vs. the mockup.
        alignItems: 'center',
        paddingTop: 5,
        backgroundColor: theme.palette.primary.contrast,
        [borderSide]: `1px solid ${theme.palette.divider}`,
      }}
    >
      {visibleLabel && (
        // Figma spec: typography/subtitle2 (14px/medium/text-primary) — the
        // VDS `subtitle2` variant already IS exactly that combination, so
        // no fontWeight override is needed at all (unlike the caption-based
        // attempt this replaces, which needed one and still read too
        // light/small — turns out the size AND color were wrong, not just
        // the weight).
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            color: theme.palette.text.primary,
            letterSpacing: '0.1px',
            userSelect: 'none',
          }}
        >
          {visibleLabel}
        </Typography>
      )}
    </Box>
  );
};
